import { useParams } from '@reach/router';
import classNames from 'classnames';
import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'urql';

import { RewardDetailQuery } from '@/blocks/reward-detail-block/__generated__/useRewardDetail';
import {
  BatchProgressBar,
  Button,
  Card,
  CardCloseButton,
  CardOptions,
  CheckboxField,
  ErrorMessage,
  FieldsetWrapper,
  NakedForm,
  PlayersSearch,
  SubmitButton,
  TextField,
  TextareaField,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import { useBatchProcessor } from '@/hooks';
import { Nullable } from '@/types';
import { assert } from '@/utils/error';

const query = graphql`
  query SanityRewardPlayerAssignForm {
    sanityRewardPlayerAssignForm {
      title {
        ...SanityLocaleString
      }
      successMessage {
        ...SanityLocaleString
      }
    }
  }
`;

const assignPlayerToRewardMutation = gql`
  mutation AssignPlayerToReward(
    $id: String!
    $comment: String!
    $playerIds: [String!]!
    $generateAssignationId: Boolean
    $assignationId: String
  ) {
    assignPlayers(
      id: $id
      comment: $comment
      playerIds: $playerIds
      generateAssignationId: $generateAssignationId
      assignationId: $assignationId
    )
  }
`;

type Props = {
  reward: RewardDetailQuery['viewer']['reward'] | null | undefined;
};

type FormValues = {
  comment: string;
  playerIds: string[];
  generateAssignationId?: boolean;
  assignationId?: string | null;
  enableBatchProcessing?: boolean;
  batchSize?: number;
};

// Batch size for processing player assignments to a reward.
// Change this value to adjust the batch size based on backend capabilities
const BATCH_SIZE = 1000;

const RewardPlayerAssignForm = ({ reward }: Props) => {
  const { t } = useTranslate();
  const staticData =
    useStaticQuery<Queries.SanityRewardPlayerAssignFormQuery>(query);

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const { close } = useModalContext();
  const params = useParams<{ rewardId: string }>();

  const form = staticData.sanityRewardPlayerAssignForm;
  assert(form, 'missing form data');

  const [assignPlayerToRewardState, assignPlayerToReward] = useMutation(
    assignPlayerToRewardMutation,
  );

  const { processBatches, progress } = useBatchProcessor<string, boolean>({
    enableFeedback: true,
  });

  const defaultValues: FormValues = {
    comment: '',
    playerIds: [],
    generateAssignationId: false,
    assignationId: null,
    enableBatchProcessing: false,
    batchSize: BATCH_SIZE,
  };

  const methods = useForm<FormValues>({ defaultValues });
  const { watch, setValue } = methods;
  const watchedValues = watch();
  const { playerIds, enableBatchProcessing } = watchedValues;

  // Automatically enable batch processing if there are more than BATCH_SIZE player IDs
  useEffect(() => {
    if (playerIds && playerIds.length > BATCH_SIZE) {
      setValue('enableBatchProcessing', true);
    }
  }, [playerIds, setValue]);

  const handleCloseButton = useCallback(() => {
    if (close) {
      close();
    }
  }, [close]);

  const processPlayers = useCallback(
    async (batch: string[], values: FormValues): Promise<boolean> => {
      const variables = {
        id: params.rewardId,
        comment: values.comment,
        playerIds: batch,
        generateAssignationId: values.generateAssignationId,
        assignationId: values.assignationId,
      };

      const res = await assignPlayerToReward(variables);

      if (res.error?.message) {
        throw new Error(res.error.message);
      }

      return true;
    },
    [assignPlayerToReward, params.rewardId],
  );

  const onSubmit = useCallback(
    async (values: FormValues) => {
      setErrorMessage(null);
      const batchSize = Number(values.batchSize) || BATCH_SIZE;

      try {
        await processBatches(values.playerIds, {
          batchSize,
          processor: (batch: string[]) => processPlayers(batch, values),
          fallbackThreshold: batchSize,
          fallbackProcessor: (playerIds: string[]) =>
            processPlayers(playerIds, values),
        });

        // Close modal for normal submissions (non-batch processing)
        if (!values.enableBatchProcessing && close) {
          close();
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error occurred';
        setErrorMessage(errorMessage);
      }
    },
    [processBatches, processPlayers, close],
  );

  if (!form) {
    return null;
  }

  const hasPlayers = playerIds && playerIds.length > 0;
  const shouldShowBatchOptions = enableBatchProcessing && hasPlayers;
  const isBatchCompleted = !progress.isProcessing && progress.totalBatches > 0;

  const isProcessing =
    progress.isProcessing || assignPlayerToRewardState.fetching;

  return (
    <Card
      size="lg"
      title={`${t(form.title)}: ${reward?.name}`}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <div className="flex p-6">
        <NakedForm className="w-full" methods={methods} onSubmit={onSubmit}>
          <div className="flex sm:flex-row flex-col space-x-6 pb-6">
            <div className="flex-1 space-y-4">
              <PlayersSearch
                name="playerIds"
                id="RewardPlayerAddForm__playerIds"
                title="Players"
              />
            </div>
            <div className="flex-1 space-y-4">
              <TextareaField
                name="comment"
                title="Note"
                id="RewardPlayerAddForm__comment"
              />
              <div className="flex flex-col space-y-3">
                <FieldsetWrapper legend="Batch Processing">
                  <CheckboxField
                    title="Enable batch processing for large player lists"
                    name="enableBatchProcessing"
                    id="RewardPlayerAddForm__enableBatchProcessing"
                  />
                  {shouldShowBatchOptions && (
                    <TextField
                      name="batchSize"
                      id="RewardPlayerAddForm__batchSize"
                      title={`Batch size (default: ${BATCH_SIZE})`}
                      type="number"
                      min="1"
                      max="5000"
                    />
                  )}
                </FieldsetWrapper>
                <FieldsetWrapper legend="Advanced Settings">
                  <TextField
                    name="assignationId"
                    id="RewardPlayerAddForm__assignationId"
                    title="Assignation ID (optional)"
                    disabled={watch('generateAssignationId')}
                  />
                  <CheckboxField
                    title="Auto generate Assignation ID"
                    name="generateAssignationId"
                    id="RewardPlayerAddForm__generateAssignationId"
                  />
                </FieldsetWrapper>
              </div>
            </div>
          </div>
          <BatchProgressBar progress={progress} className="mb-6" />
          <ErrorMessage message={errorMessage} />
          <div
            className={classNames('flex', {
              'justify-between': isBatchCompleted,
              'justify-end': !isBatchCompleted,
            })}
          >
            {isBatchCompleted && (
              <div className="flex space-x-3">
                <Button
                  variant="secondary"
                  onClick={handleCloseButton}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Close
                </Button>
              </div>
            )}
            <SubmitButton
              value={isProcessing ? 'Processing...' : 'Assign'}
              disabled={isProcessing || isBatchCompleted || !hasPlayers}
            />
          </div>
        </NakedForm>
      </div>
    </Card>
  );
};

export default RewardPlayerAssignForm;
